import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench, Plus, CircleNotch, MapPin, Bell, UserCircle, ArrowRight,
  CheckCircle, Warning, Clock, MagnifyingGlass, Tag
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ToolCard({ tool, onCheckout, onCheckin, onAlert }) {
  const isOut = tool.status === "checked_out";
  const isCustom = tool.is_custom;
  return (
    <div data-testid={`tool-${tool.id}`} className={`wp-card animate-slide-up ${isCustom ? "border-l-2 border-l-[#34C759]" : ""}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${isCustom ? "bg-[#34C75910]" : "bg-[#F4F4F5]"}`}>
            {isCustom ? <Tag size={16} className="text-[#34C759]" /> : <Wrench size={16} className="text-[#404040]" />}
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>{tool.name}</h3>
            <div className="flex items-center gap-2 font-mono-data text-[10px] text-[#404040]">
              {tool.serial_number && <span>SN: {tool.serial_number}</span>}
              {isCustom && <span className="text-[#34C759]">IN-HOUSE</span>}
            </div>
          </div>
        </div>
        <span className={`font-mono-data text-[10px] px-1.5 py-0.5 rounded-sm ${
          isOut ? "text-[#FF5A00] bg-[#FF5A0010] border border-[#FF5A00]" : "text-[#34C759] bg-[#34C75910] border border-[#34C759]"
        }`}>
          {isOut ? "CHECKED OUT" : "AVAILABLE"}
        </span>
      </div>
      <div className="flex items-center gap-3 text-[10px] font-mono-data text-[#404040] mb-2">
        <span className="flex items-center gap-0.5"><MapPin size={8} /> {tool.location?.toUpperCase()}</span>
        {tool.assigned_to && <span className="flex items-center gap-0.5"><UserCircle size={8} /> {tool.assigned_to}</span>}
      </div>
      {isCustom && tool.design_notes && <p className="text-[11px] text-[#404040] mb-2 italic">{tool.design_notes}</p>}
      <div className="flex gap-2">
        {!isOut ? (
          <button data-testid={`checkout-${tool.id}`} onClick={() => onCheckout(tool.id)}
            className="flex-1 py-1.5 text-[10px] font-bold bg-[#002FA7] text-white rounded-sm flex items-center justify-center gap-1">
            <ArrowRight size={10} /> Check Out
          </button>
        ) : (
          <button data-testid={`checkin-${tool.id}`} onClick={() => onCheckin(tool.id)}
            className="flex-1 py-1.5 text-[10px] font-bold bg-[#34C759] text-white rounded-sm flex items-center justify-center gap-1">
            <CheckCircle size={10} /> Check In
          </button>
        )}
        <button onClick={() => onAlert(tool)} className="px-3 py-1.5 border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A]">
          <Bell size={12} />
        </button>
      </div>
    </div>
  );
}

export default function ToolTrackerPage() {
  const [tools, setTools] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedToolId, setSelectedToolId] = useState("");
  const [search, setSearch] = useState("");
  const [worker, setWorker] = useState("");
  const [form, setForm] = useState({ name: "", serial_number: "", category: "electrical", is_custom: false, designed_by: "", design_notes: "", alert_if_missing_hours: 24 });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [toolsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/tools/tracked`),
        axios.get(`${API}/tools/alerts`),
      ]);
      setTools(toolsRes.data);
      setAlerts(alertsRes.data);
    } catch { toast.error("Failed to load tools"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createTool = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tools/tracked`, form);
      toast.success("Tool added");
      setCreateOpen(false);
      setForm({ name: "", serial_number: "", category: "electrical", is_custom: false, designed_by: "", design_notes: "", alert_if_missing_hours: 24 });
      fetchData();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const handleCheckout = (toolId) => { setSelectedToolId(toolId); setCheckoutOpen(true); };
  const confirmCheckout = async () => {
    if (!worker) { toast.error("Enter worker name"); return; }
    try {
      await axios.post(`${API}/tools/tracked/${selectedToolId}/checkout`, { worker, location: "field" });
      toast.success("Tool checked out");
      setCheckoutOpen(false); setWorker(""); fetchData();
    } catch { toast.error("Failed"); }
  };
  const handleCheckin = async (toolId) => {
    try {
      await axios.post(`${API}/tools/tracked/${toolId}/checkin`);
      toast.success("Tool checked in");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const triggerAlert = (tool) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(`Tool Alert: ${tool.name}`, { body: `${tool.name} is ${tool.status === "checked_out" ? `checked out to ${tool.assigned_to}` : "available at " + tool.location}`, icon: "/favicon.ico" });
    } else if ("Notification" in window) {
      Notification.requestPermission().then(p => {
        if (p === "granted") new Notification(`Tool Alert: ${tool.name}`, { body: `Status: ${tool.status}` });
      });
    }
    // Also play a beep
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); osc.type = "sine"; osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.connect(ctx.destination); osc.start(); osc.stop(ctx.currentTime + 0.3);
    } catch {}
    toast.info(`Alert: ${tool.name} - ${tool.status}`);
  };

  const filtered = tools.filter(t => !search || t.name.toLowerCase().includes(search.toLowerCase()));
  const customTools = tools.filter(t => t.is_custom);

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="tool-tracker-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>Tool Tracker</h1>
          <p className="text-[10px] font-mono-data text-[#404040]">CHECK IN/OUT & ALERTS</p>
        </div>
        <button data-testid="add-tool-btn" onClick={() => setCreateOpen(true)}
          className="touch-target w-10 h-10 bg-[#002FA7] text-white rounded-sm flex items-center justify-center"><Plus size={20} weight="bold" /></button>
      </div>

      {alerts.length > 0 && (
        <div className="wp-card mb-4 bg-[#FF5A0008] border-[#FF5A00]" data-testid="tool-alerts">
          <span className="font-mono-data text-[10px] text-[#FF5A00] flex items-center gap-1"><Warning size={10} /> {alerts.length} TOOL(S) OVERDUE</span>
          {alerts.map(a => <p key={a.id} className="text-xs text-[#404040] mt-1">{a.name} - {a.hours_out}h with {a.assigned_to}</p>)}
        </div>
      )}

      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#404040]" />
        <input data-testid="tool-search" type="text" placeholder="Search tools..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
      </div>

      <Tabs defaultValue="all">
        <TabsList className="grid w-full grid-cols-2 h-9 mb-3">
          <TabsTrigger value="all" className="text-xs" data-testid="tools-all-tab">All Tools ({tools.length})</TabsTrigger>
          <TabsTrigger value="custom" className="text-xs" data-testid="tools-custom-tab">In-House ({customTools.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          {loading ? <div className="flex justify-center py-12"><CircleNotch size={28} className="animate-spin text-[#002FA7]" /></div> :
          filtered.length === 0 ? <div className="wp-card text-center py-8 text-sm text-[#404040]">No tools tracked yet.</div> :
          <div className="space-y-3">{filtered.map((t,i) => <div key={t.id} style={{animationDelay:`${i*0.05}s`}}><ToolCard tool={t} onCheckout={handleCheckout} onCheckin={handleCheckin} onAlert={triggerAlert} /></div>)}</div>}
        </TabsContent>
        <TabsContent value="custom">
          {customTools.length === 0 ? <div className="wp-card text-center py-8 text-sm text-[#404040]">No in-house tools yet. Add one with "In-House Design" checked.</div> :
          <div className="space-y-3">{customTools.map((t,i) => <div key={t.id} style={{animationDelay:`${i*0.05}s`}}><ToolCard tool={t} onCheckout={handleCheckout} onCheckin={handleCheckin} onAlert={triggerAlert} /></div>)}</div>}
        </TabsContent>
      </Tabs>

      {/* Create Tool */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Add Tool</DialogTitle></DialogHeader>
          <form onSubmit={createTool} className="space-y-3">
            <input data-testid="tool-name-input" type="text" placeholder="Tool name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            <input type="text" placeholder="Serial number (optional)" value={form.serial_number} onChange={e => setForm({...form, serial_number: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="hvac">HVAC</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_custom} onChange={e => setForm({...form, is_custom: e.target.checked})} className="rounded-sm" />
              <label className="text-xs font-medium">In-House Design (custom tool)</label>
            </div>
            {form.is_custom && (
              <>
                <input type="text" placeholder="Designed by" value={form.designed_by} onChange={e => setForm({...form, designed_by: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
                <textarea placeholder="Design notes / description" value={form.design_notes} onChange={e => setForm({...form, design_notes: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-16" />
              </>
            )}
            <div>
              <label className="text-[10px] font-mono-data text-[#404040]">ALERT IF MISSING (HOURS)</label>
              <input type="number" value={form.alert_if_missing_hours} onChange={e => setForm({...form, alert_if_missing_hours: parseInt(e.target.value) || 24})}
                className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            </div>
            <button data-testid="create-tool-submit" type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm disabled:opacity-50">{saving ? "Adding..." : "Add Tool"}</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Check Out Tool</DialogTitle></DialogHeader>
          <input data-testid="checkout-worker-input" type="text" placeholder="Worker name" value={worker} onChange={e => setWorker(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] mb-3" />
          <button data-testid="confirm-checkout-btn" onClick={confirmCheckout}
            className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm">Confirm Checkout</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
